import { CSSProperties, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import useBrand from 'Hooks/useBrand';
import { getBillingPortalUrl_api } from 'api/paymentsV2Api';
import { goToUrl } from 'modules/Utils';
import { SHARED_TOAST_OPTIONS_ERROR } from 'modules/Toast';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import useUser from 'Hooks/useUser';

interface PaymentPastDueModalProps {
  /** Whether to show the modal automatically when past due is detected */
  autoShow?: boolean;
  /** Manual control over modal visibility - overrides autoShow when provided */
  isOpen?: boolean;
  /** Callback when modal is closed */
  onClose?: () => void;
}

const PaymentPastDueModal = ({
  autoShow = true,
  isOpen,
  onClose,
}: PaymentPastDueModalProps) => {
  const { currentBrand, getIsPaymentPastDue } = useBrand();
  const { isSymphonyAdmin } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);

  // Helper to determine current payment status
  const getPaymentStatus = (): 'past_due' | 'paused' | null => {
    const subscriptionStatus = currentBrand?.subscription_status || currentBrand?.subscription?.status;
    if (subscriptionStatus === 'past_due') return 'past_due';
    if (subscriptionStatus === 'paused') return 'paused';
    return null;
  };

  // Get appropriate messaging based on payment status
  const getModalContent = () => {
    const status = getPaymentStatus();
    if (status === 'paused') {
      return {
        title: 'Add a payment method to continue.',
        description: 'Your trial has ended and your subscription is paused. Add a payment method to reactivate your subscription and continue using all features.'
      };
    }
    // Default to past_due messaging
    return {
      title: 'Your subscription is past due.',
      description: 'Your recent payment didn\'t go through successfully. To ensure your account remains active, please update your payment method or manage your subscription.'
    };
  };

  // Auto-show logic when isOpen is not manually controlled
  useEffect(() => {
    if (isOpen !== undefined) {
      return; // Manual control takes precedence
    }

    let shouldShowModal = getPaymentStatus() !== null
    console.log('shouldShowModal', shouldShowModal)

    // Only show modal if:
    // 1. autoShow is enabled
    // 2. Payment is past due
    // 3. Haven't shown it this session yet
    // 4. Brand has a subscription
    if (autoShow && shouldShowModal && !hasShownThisSession && currentBrand?.subscription) {
      setShowModal(true);
      setHasShownThisSession(true);
    }
  }, [autoShow, hasShownThisSession, currentBrand?.subscription, isOpen]);

  const handleUpdateBilling = async () => {
    if (!currentBrand?.id) {
      toast.error('Unable to access billing details. Please try again.', SHARED_TOAST_OPTIONS_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      const url = await getBillingPortalUrl_api({ brandId: currentBrand.id });
      goToUrl(url, setIsLoading);
      handleCloseModal(); // Close modal after redirecting
    } catch (error) {
      setIsLoading(false);
      Sentry.captureException(error);
      console.error('Error accessing billing portal:', error);
      toast.error('Something went wrong. Please try again or contact support.', SHARED_TOAST_OPTIONS_ERROR);
    }
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    setShowModal(false);
  };

  // Use manual control if provided, otherwise use auto-show state
  const modalIsOpen = isOpen !== undefined ? isOpen : showModal;
  const modalContent = getModalContent();

  return (
    <ModalSymphony
      size="medium"
      className="paymentPastDueModal"
      isOpen={modalIsOpen}
      onClose={handleCloseModal}
      isHideCloseButton={!isSymphonyAdmin}
    >
      <div className="paymentPastDueModalContent" style={sty.content}>
        <div className="paymentPastDueModalHeader" style={sty.header}>
          <Icon icon="warning" size={36} color={Colors.red} className="paymentPastDueModalIcon" />
          <h2 style={sty.title}>{modalContent.title}</h2>
        </div>
        
        <div className="paymentPastDueModalBody" style={sty.body}>
          <p style={Sty.text16}>
            {modalContent.description}
          </p>
        </div>
        
        <div className="paymentPastDueModalActions" style={sty.actions}>
          <ButtonSymphony
            className="paymentPastDueModalUpdateButton"
            onClick={handleUpdateBilling}
            isLoading={isLoading}
            disabled={isLoading}
            variant="primary"
            color="purple"
            style={sty.primaryButton}
          >
            {isLoading ? 'Opening Billing Portal...' : (getPaymentStatus() === 'paused' ? 'Add Payment Method' : 'Manage Billing & Payment')}
          </ButtonSymphony>
        </div>
      </div>
    </ModalSymphony>
  );
};

const sty: Record<string, CSSProperties> = {
  content: {
    width: '100%',
    textAlign: 'center'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '0 0 8px 0',
    fontFamily: 'DIN, sans-serif',
  },
  body: {
    marginBottom: '32px',
  },
  description: {
    fontSize: '16px',
    lineHeight: '1.5',
    color: '#4B5563',
    margin: '0 0 16px 0',
    fontFamily: 'DIN, sans-serif',
  },
  subDescription: {
    fontSize: '14px',
    lineHeight: '1.4',
    color: '#6B7280',
    margin: '0',
    fontFamily: 'DIN, sans-serif',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
  },
  primaryButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
  },
  secondaryButton: {
    fontSize: '14px',
    color: '#6B7280',
  },
};

export default PaymentPastDueModal; 